import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Lock, ArrowRight, Users, GitBranch, BarChart3, Bell, ShieldCheck, RotateCw } from "lucide-react";

const FEATURES = [
  { icon: Users, title: "Lead Management", sub: "Capture, qualify & assign in seconds" },
  { icon: GitBranch, title: "Pipeline & Funnel", sub: "Track every opportunity through to close" },
  { icon: BarChart3, title: "Performance Insights", sub: "Conversion, revenue and team analytics" },
  { icon: Bell, title: "Smart Reminders", sub: "Daily digest, follow-ups, owner alerts" },
];

export default function Login() {
  const { login, verifyMfa, resendMfa, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("password"); // "password" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) { setError(r.error || "Sign in failed"); return; }
    if (r.mfa_required) {
      setMfaToken(r.mfa_token);
      setStep("otp");
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* LEFT panel */}
      <div className="bg-white p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div className="relative">
          <div className="flex items-center gap-4">
            <img src="/favicon.png" alt="EYB" className="w-14 h-14" />
            <div>
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#1f8ebf]">EYB Consulting Pty Ltd</div>
              <div className="font-heading text-2xl font-extrabold text-slate-900 leading-tight">EYB CRM</div>
            </div>
          </div>

          <h1 className="mt-14 font-heading text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[0.95] tracking-tight">
            Your pipeline,<br />in one place.
          </h1>
          <p className="mt-5 text-slate-500 text-base max-w-md leading-relaxed">
            Capture leads, manage opportunities, track conversion and forecast revenue — all from a single, secure portal.
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-[#29ABE2]/10 text-[#187199] border border-[#29ABE2]/30">Role-based access</span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">Full audit trail</span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">AUD reporting</span>
          </div>
        </div>

        <div className="relative mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
          {FEATURES.map((s) => (
            <div key={s.title} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-[#29ABE2]/40 hover:bg-slate-50/40 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#29ABE2]/10 to-[#1B4F8B]/10 flex items-center justify-center shrink-0">
                <s.icon className="w-4 h-4 text-[#1B4F8B]" />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-[14px] font-bold text-slate-900 leading-tight">{s.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{s.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="relative text-[10px] tracking-[0.18em] uppercase text-slate-400 mt-10">
          Secure CRM Portal · v1.0
        </div>
      </div>

      {/* RIGHT panel */}
      <div className="bg-[#0B1521] p-10 lg:p-16 flex items-center justify-center relative overflow-hidden">
        {step === "password" ? (
          <form onSubmit={submit} className="relative w-full max-w-sm" data-testid="login-form">
            <h2 className="font-heading text-4xl font-bold text-white">Welcome</h2>
            <p className="text-slate-400 text-sm mt-2">Sign in to access your CRM</p>

            <div className="mt-10 space-y-5">
              <div>
                <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-400 block mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full bg-slate-100 rounded-lg pl-10 pr-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]"
                    placeholder="you@eybconsulting.com.au"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-400 block mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    data-testid="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="w-full bg-slate-100 rounded-lg pl-10 pr-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]"
                    placeholder="••••••••••"
                  />
                </div>
              </div>

              {error && <div data-testid="login-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2">{error}</div>}

              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#29ABE2] hover:bg-[#1f8ebf] disabled:opacity-60 text-white font-semibold rounded-full py-3 transition-colors"
              >
                {loading ? "Signing in…" : "Continue"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-10 pt-6 border-t border-white/10 text-xs text-slate-500">
              Secured with two-step verification. We&apos;ll email you a one-time code after your password is verified.
            </div>
          </form>
        ) : (
          <OtpStep
            email={email}
            mfaToken={mfaToken}
            onVerify={verifyMfa}
            onResend={resendMfa}
            onBack={() => { setStep("password"); setError(""); }}
            onSuccess={() => navigate("/dashboard")}
          />
        )}
      </div>
    </div>
  );
}

function OtpStep({ email, mfaToken: initialToken, onVerify, onResend, onBack, onSuccess }) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [mfaToken, setMfaToken] = useState(initialToken);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(60);
  const refs = useRef([]);

  useEffect(() => { refs.current[0]?.focus(); }, []);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const setDigit = (idx, val) => {
    const v = (val || "").replace(/\D/g, "").slice(0, 1);
    setDigits((cur) => { const n = [...cur]; n[idx] = v; return n; });
    if (v && idx < 5) refs.current[idx + 1]?.focus();
  };
  const onKey = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) refs.current[idx - 1]?.focus();
    if (e.key === "ArrowLeft" && idx > 0) refs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) refs.current[idx + 1]?.focus();
  };
  const onPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const arr = ["", "", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) arr[i] = txt[i];
    setDigits(arr);
    refs.current[Math.min(txt.length, 5)]?.focus();
    if (txt.length === 6) doVerify(txt);
  };

  const doVerify = async (codeStr) => {
    const code = codeStr || digits.join("");
    if (code.length !== 6) { setError("Enter all 6 digits"); return; }
    setSubmitting(true); setError(""); setInfo("");
    const r = await onVerify(mfaToken, code);
    setSubmitting(false);
    if (r.ok) onSuccess();
    else { setError(r.error || "Verification failed"); setDigits(["", "", "", "", "", ""]); refs.current[0]?.focus(); }
  };

  const handleResend = async () => {
    if (resendIn > 0) return;
    setError(""); setInfo("");
    const r = await onResend(mfaToken);
    if (r.ok) {
      setMfaToken(r.mfa_token);
      setInfo("A new code has been sent to your inbox.");
      setResendIn(60);
      setDigits(["", "", "", "", "", ""]);
      refs.current[0]?.focus();
    } else {
      setError(r.error || "Could not resend code");
    }
  };

  const submit = (e) => { e.preventDefault(); doVerify(); };

  return (
    <form onSubmit={submit} className="relative w-full max-w-sm" data-testid="otp-form">
      <div className="flex items-center gap-2 text-[#29ABE2] text-xs font-semibold tracking-wider uppercase mb-3">
        <ShieldCheck className="w-4 h-4" /> Two-step verification
      </div>
      <h2 className="font-heading text-3xl font-bold text-white">Enter the 6-digit code</h2>
      <p className="text-slate-400 text-sm mt-2">
        We sent a code to <span className="text-white font-semibold">{email}</span>. It expires in 10 minutes.
      </p>

      <div className="mt-8 flex gap-2 justify-between" data-testid="otp-inputs">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (refs.current[i] = el)}
            data-testid={`otp-digit-${i}`}
            value={d}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => onKey(i, e)}
            onPaste={i === 0 ? onPaste : undefined}
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            className="w-12 h-14 text-center text-2xl font-bold rounded-lg bg-slate-100 text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]"
          />
        ))}
      </div>

      {error && <div data-testid="otp-error" className="mt-4 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2">{error}</div>}
      {info && <div className="mt-4 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-3 py-2">{info}</div>}

      <button
        data-testid="otp-verify-btn"
        type="submit"
        disabled={submitting || digits.some((d) => !d)}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#29ABE2] hover:bg-[#1f8ebf] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-full py-3 transition-colors"
      >
        {submitting ? "Verifying…" : "Verify & sign in"}
        <ArrowRight className="w-4 h-4" />
      </button>

      <div className="mt-6 flex items-center justify-between text-xs">
        <button
          type="button"
          data-testid="otp-resend-btn"
          onClick={handleResend}
          disabled={resendIn > 0}
          className="inline-flex items-center gap-1.5 text-[#29ABE2] disabled:text-slate-500 hover:underline disabled:no-underline"
        >
          <RotateCw className="w-3 h-3" />
          {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
        </button>
        <button
          type="button"
          data-testid="otp-back-btn"
          onClick={onBack}
          className="text-slate-400 hover:text-white"
        >
          Use a different account
        </button>
      </div>
    </form>
  );
}
